import { useEffect, useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { mutationErrorMessage, normalizeApiError } from '@/features/common/apiError';
import { useGetOrgProjectsQuery } from '@/features/orgs/api/orgApi';
import { docId } from '@/features/agents/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateNumberMutation } from '../api/numbersApi';
import { E164_FORMAT_ERROR, isValidE164, normalizeE164 } from '../utils';

const NO_PROJECT = 'none';

export interface RegisterNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  /** The project currently applied as a list filter preselects the form (G4). */
  defaultProjectId?: string;
}

/**
 * Register-number dialog (design §7). `POST /v1/voice-bridge/numbers` —
 * E.164 client gate mirrors the server normalization; 409 renders the neutral
 * server wording with no org attribution (audit #3); `isActive` defaults true
 * server-side, so NO activation control lives here (H3 — one toggle surface,
 * §10 owns it). The dialog never discards input on error.
 */
export function RegisterNumberDialog({
  open,
  onOpenChange,
  orgId,
  defaultProjectId,
}: RegisterNumberDialogProps) {
  const toast = useToast();
  const [createNumber, { isLoading }] = useCreateNumberMutation();
  const { data: projects } = useGetOrgProjectsQuery(orgId);

  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [projectId, setProjectId] = useState<string>(defaultProjectId ?? NO_PROJECT);
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Re-seed the project preselect each time the dialog opens (§7 G4).
  useEffect(() => {
    if (open) {
      setProjectId(defaultProjectId ?? NO_PROJECT);
    }
  }, [open, defaultProjectId]);

  async function handleSubmit() {
    const normalized = normalizeE164(number);
    if (!isValidE164(normalized)) {
      setFieldError(E164_FORMAT_ERROR);
      return;
    }
    setFieldError(null);
    try {
      await createNumber({
        number: normalized,
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(projectId !== NO_PROJECT ? { projectId } : {}),
      }).unwrap();
      toast.success('Number registered.');
      setNumber('');
      setLabel('');
      onOpenChange(false);
    } catch (error) {
      const normalizedError = normalizeApiError(error);
      if (normalizedError.status === 409) {
        setFieldError('This number is already registered.');
      } else if (normalizedError.status === 400) {
        setFieldError(normalizedError.serverMessage ?? E164_FORMAT_ERROR);
      } else {
        toast.error(mutationErrorMessage(error, "Couldn't register number."));
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isLoading) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Register number</DialogTitle>
          <DialogDescription>
            Add a phone number for this organization. Numbers forward calls to the agent they are
            bound to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="register-number">Phone number</Label>
            <Input
              id="register-number"
              className="font-mono"
              value={number}
              onChange={(event) => {
                setNumber(event.target.value);
                if (fieldError) {
                  setFieldError(null);
                }
              }}
              onBlur={() => {
                const normalized = normalizeE164(number);
                if (normalized && !isValidE164(normalized)) {
                  setFieldError(E164_FORMAT_ERROR);
                } else if (isValidE164(normalized)) {
                  setNumber(normalized);
                  setFieldError(null);
                }
              }}
              placeholder="e.g. +14155550123"
              disabled={isLoading}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? 'register-number-error' : 'register-number-help'}
            />
            {fieldError ? (
              <p
                id="register-number-error"
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
              >
                {fieldError}
              </p>
            ) : (
              <p id="register-number-help" className="text-sm text-muted-foreground">
                International format with country code.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="register-label">Label (optional)</Label>
            <Input
              id="register-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="e.g. Support line"
              disabled={isLoading}
            />
            <p className="text-sm text-muted-foreground">
              A short name shown in lists and call logs.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="register-project">Project (optional)</Label>
            <Select value={projectId} onValueChange={setProjectId} disabled={isLoading}>
              <SelectTrigger id="register-project">
                <SelectValue placeholder="No project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>No project</SelectItem>
                {(projects ?? [])
                  .filter((project) => project.isActive !== false)
                  .map((project) => {
                    const id = docId(project);
                    return id ? (
                      <SelectItem key={id} value={id}>
                        {project.name ?? id}
                      </SelectItem>
                    ) : null;
                  })}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">Projects in this organization.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isLoading}>
            {isLoading ? <LoadingSpinner size="sm" className="mr-2" label="Registering" /> : null}
            {isLoading ? 'Registering…' : 'Register number'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
